import Vue from 'vue'
import Router from 'vue-router'
import firebase from 'firebase/app'
import Cookies from 'js-cookie'
import routes from './routes'
import store from '@/state/store.js'
import 'firebase/auth'

// Lib - helper functions
import { getFirebaseUserStatus } from '@/lib/firebase'
import { fetchStarlingData } from '@/lib/starling'

// @TODO: consider using some of this functionality from enterprise boilerplate
// https://github.com/declandewet/vue-meta
// import VueMeta from 'vue-meta'
// Adds a loading bar at the top during page loads.
// import NProgress from 'nprogress/nprogress'

Vue.use(Router)

const router = new Router({
  mode: 'history',
  routes,
})

router.beforeEach((routeTo, routeFrom, next) => {
  const isInitialLoad = routeFrom.name === null && !store.getters.getStoreReady

  if (isInitialLoad) {
    // Setup firebase
    firebase.initializeApp({
      apiKey: process.env.VUE_APP_FIREBASE_API_KEY,
      authDomain: process.env.VUE_APP_FIREBASE_AUTH_DOMAIN,
      projectId: process.env.VUE_APP_FIREBASE_PROJECT_ID,
    })
    getFirebaseUserStatus()
      .then((user) => {
        // Wait for the user token to be available for api requests
        store.dispatch('user/autoSignIn', user).then(() => {
          handleQuoteIdParam()
          store
            .dispatch('initialiseStore')
            // check if starling data to be fetched
            .then(() => {
              fetchStarlingData().catch()
              handleParams()
            })
        })
      })
      .catch(() => {
        handleQuoteIdParam()
        store
          .dispatch('initialiseStore')
          // check if starling data to be fetched
          .then(() => {
            handleParams()
          })
      })
  } else {
    // Custom UI Journeys
    journeyAppearHere()
    return next()
  }

  function handleQuoteIdParam () {
    // @TODO - Bit of a hack for handling 'quote' param
    if (routeTo.query.quote) {
      Cookies.set('dr-store', JSON.stringify({ quoteId: routeTo.query.quote }), { expires: 2 })
    }

    store.dispatch('initialiseStore').then(() => {
      handleParams()
    })
  }

  // return next({ name: 'error-page', query: { redirectFrom: routeTo.fullPath } })

  function handleParams () {
    const couponIdParam = routeTo.query.promocode
    const referrerParam = routeTo.query.referrer
    const sourceParam = routeTo.query.source
    const industryParam = routeTo.query.industry

    if (couponIdParam) store.commit('updateCouponId', couponIdParam.split(','))
    if (referrerParam) store.commit('updateReferrer', referrerParam)
    if (sourceParam) store.commit('updateSource', sourceParam)

    if (industryParam) {
      store.commit('updateIndustryFromParam', true)
      store.dispatch('updateIndustry', industryParam)
    }

    handleLoaded()
    handleRedirects()
  }

  function handleLoaded () {
    // Check the store not already loaded

    store.commit('updateStoreReady', true) /* store is now ready, App.vue uses this to load app once ready */
    const quoteReference = store.getters.getReference
    const products = store.getters.getProducts

    // calculate total number of products configured (to be send to parent window for launcher)
    let totalProducts = 0
    products.forEach((product) => {
      product.rates.forEach((rate) => {
        if (rate.key !== 'CommercialCombined\\MinimumPremiumAdjustment') totalProducts += 1
      })
    })
    // post message from iframe to parent window to handle launcher status
    window.parent.postMessage(
      {
        type: 'status-sync',
        payload: {
          from: 'dr-widget',
          quoteActive: !!quoteReference,
          totalProducts,
        },
      },
      '*',
    )
  }

  function handleRedirects () {
    const accountReference = store.getters.getConvertedToAccountRef
    const quoteReference = store.getters.getReference
    const quoteId = store.getters.getQuoteId

    // as :pid/:rid can't be matched in router, we're redirecting info and config screens
    if (routeTo.path.startsWith('/products/')) {
      return next({
        name: 'products-list',
        query: {
          redirected: true,
        },
      })
    }

    if (routeTo.name.startsWith('user-')) return next() /* login page */
    if (routeTo.query.account) return next() /* quote has been converted and redirected from gocardless */

    // Don't redirect user if already been redirected
    if (routeTo.query.redirected) return next()

    if (accountReference && !store.state.portal) {
      return next({
        name: 'quote-successful',
        query: {
          redirected: true,
        },
      }) /* quote converted */
    }

    // if quote has progressed to product config or further, go to product list page

    if (quoteId && quoteReference) {
      // Ensure turnover if missing
      if (!store.getters.getTurnover) {
        return next({
          name: 'turnover',
          query: {
            redirected: true,
          },
        })
      }

      // Ensure employeeCount if missing
      if (!store.getters.getEmployeeCount) {
        return next({
          name: 'employee count',
          query: {
            redirected: true,
          },
        })
      }

      return next({
        name: 'products-list',
        query: {
          redirected: true,
        },
      })
    } else {
      return next({
        name: 'home',
        query: {
          redirected: true,
        },
      })
    }
  }

  function journeyAppearHere () {
    // Popup Offices - Appear Here
    if (store.getters.getSource === 'appear-here') {
      // Industry can only be Pop up Shop
      store.dispatch('updateIndustry', 'b3517ff9-5ca7-4738-bd25-fdf5cd075775')
      // Payment is lump sum
      store.commit('updatePaymentLumpSum', true)

      // Business activites can't be changed
      if (routeTo.name === 'business activities') {
        const name = routeFrom.meta.page < routeTo.meta.page ? 'turnover' : 'business lookup'
        return next({ name })
      }

      // Policy start date is set by Popup Cover start date
      if (routeTo.name === 'policy start date') {
        const name = routeFrom.meta.page < routeTo.meta.page ? 'billing details' : 'policy holder details'
        return next({ name })
      }
    }
  }
})

// router.beforeResolve(async (routeTo, routeFrom, next) => {
//   // Create a `beforeResolve` hook, which fires whenever
//   // `beforeRouteEnter` and `beforeRouteUpdate` would. This
//   // allows us to ensure data is fetched even when params change,
//   // but the resolved route does not. We put it in `meta` to
//   // indicate that it's a hook we created, rather than part of
//   // Vue Router (yet?).
//   try {
//     // For each matched route...
//     for (const route of routeTo.matched) {
//       await new Promise((resolve, reject) => {
//         // If a `beforeResolve` hook is defined, call it with
//         // the same arguments as the `beforeEnter` hook.
//         if (route.meta && route.meta.beforeResolve) {
//           route.meta.beforeResolve(routeTo, routeFrom, (...args) => {
//             // If the user chose to redirect...
//             if (args.length) {
//               // If redirecting to the same route we're coming from...
//               if (routeFrom.name === args[0].name) {
//                 // Complete the animation of the route progress bar.
//                 NProgress.done()
//               }
//               // Complete the redirect.
//               next(...args)
//               reject(new Error('Redirected'))
//             } else {
//               resolve()
//             }
//           })
//         } else {
//           // Otherwise, continue resolving the route.
//           resolve()
//         }
//       })
//     }
//     // If a `beforeResolve` hook chose to redirect, just return.
//   } catch (error) {
//     return
//   }

//   // If we reach this point, continue resolving the route.
//   next()
// })

// // When each route is finished evaluating...
// router.afterEach((routeTo, routeFrom) => {
//   // Complete the animation of the route progress bar.
//   NProgress.done()
// })

export default router
